/**
 * Color resolution chain for `RenderableEntity.colorHex`.
 *
 * # AutoCAD's resolution rules
 *
 *   1. Entity carries a literal ACI index (e.g. `5` = Blue) → use it.
 *   2. Entity carries `BYBLOCK` → walk up to the enclosing INSERT, take
 *      its colour, then re-resolve recursively. When the entity is at
 *      model-root scope BYBLOCK falls back to the layer colour
 *      (matches AutoCAD's "no enclosing block" behaviour).
 *   3. Entity carries `BYLAYER` (or no colour at all) → look up the
 *      layer record and take its colour. If the layer record itself
 *      carries `BYLAYER` (defensive — should never happen in valid
 *      DXF), fall back to ACI 7.
 *   4. ACI 7 is not a fixed colour — it is the drawing-area FOREGROUND
 *      (white on a dark background, black on a light one). Every resolver
 *      therefore takes a `foregroundHex`; `./layoutForeground.ts` owns that
 *      rule, its ezdxf reference and the paper-space bug it closes, and is
 *      where the per-space value comes from.
 *
 * Adapters call `resolveColorHex` once per entity and store the
 * resulting `#rrggbb` on `RenderableEntity.colorHex`. The renderer
 * never re-resolves — that would cost an O(N) hash lookup on every
 * frame.
 */

import { coerceColor, type DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { aciToHex } from "./aciColorMap";
import { MODEL_SPACE_FOREGROUND_HEX } from "./layoutForeground";

/** Layer color record consulted when an entity carries `BYLAYER`. */
export interface LayerColorRecord {
  name: string;
  /** Raw colour string from the layer table (e.g. "5" for ACI 5, or "#ff0000"). */
  color: string;
}

/** Look-up table indexed by case-insensitive layer name. */
export type LayerColorMap = ReadonlyMap<string, LayerColorRecord>;

/**
 * Default fallback when every level of the chain returns nothing usable, AND
 * the default `foregroundHex` — i.e. the MODEL-space foreground. Every
 * resolver takes `foregroundHex` with this default so a caller that has not
 * been taught about paper space stays byte-identical.
 */
const FALLBACK_HEX = MODEL_SPACE_FOREGROUND_HEX;

/**
 * The AutoCAD colour index that means "the drawing-area foreground" —
 * white on a dark background, black on a light one (ezdxf
 * `RenderContext._aci_to_true_color`'s `if aci == 7` arm).
 */
const ACI_FOREGROUND = 7;

/**
 * Parse a raw colour string emitted by either the entity property bag
 * or the layer table.
 *
 * Accepted shapes (per existing scene helpers):
 *   - `"5"` / `"  5  "`     — ACI index as decimal text
 *   - `"#ff0000"` / `"#FF0000"` — explicit hex (with or without `#`)
 *   - `"ff0000"`             — bare hex (legacy editor output)
 *
 * Returns `null` for anything that does not parse into a renderable
 * colour — the caller decides what fallback applies.
 */
function parseLiteralColor(
  raw: string,
  foregroundHex: string = FALLBACK_HEX,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Hex with leading '#'
  if (trimmed.startsWith("#")) {
    return /^#[0-9a-f]{6}$/i.test(trimmed) ? trimmed.toLowerCase() : null;
  }
  // Bare 6-digit hex (no '#')
  if (/^[0-9a-f]{6}$/i.test(trimmed)) {
    return `#${trimmed.toLowerCase()}`;
  }
  // ACI index
  const aci = Number(trimmed);
  if (Number.isFinite(aci) && Number.isInteger(aci)) {
    // ACI 7 is the background CONTRAST colour, not a palette entry — see
    // `./layoutForeground.ts` for the rule. This is the string-path
    // twin of `aciIndexToHex`'s 7-arm; both must agree or the legacy bag and
    // the typed union would diverge on the one index that matters.
    if (aci === ACI_FOREGROUND) return foregroundHex;
    const hex = aciToHex(aci);
    if (hex) return hex.toLowerCase();
  }
  return null;
}

/**
 * Shared BYLAYER / BYBLOCK walk — the ONE truth both the legacy string-bag
 * resolver (`resolveColorHex`) and the union-direct typed resolver
 * (`resolveColorHexFromEntity`) call when an entity's colour defers to its
 * parent INSERT (BYBLOCK) or its layer (BYLAYER). Keeping the walk in one
 * place avoids a jscpd clone between the two resolvers AND guarantees they
 * stay byte-identical for the dominant common cases.
 *
 * Chain (matches AutoCAD + the prior inline legacy logic exactly):
 *   1. BYBLOCK — when `blockColor` is non-null, `parseLiteralColor(blockColor)`
 *      and return it if it parses. A BYLAYER caller passes `blockColor = null`
 *      so this step is skipped (byte-identical to the legacy BYLAYER branch,
 *      which never consulted the parent INSERT colour).
 *   2. BYLAYER — `layerColors.get(layerName.toLowerCase())` →
 *      `parseLiteralColor(record.color)`.
 *   3. Fallback — `FALLBACK_HEX` when no level produced a usable colour.
 *
 * @param layerName   Entity's layer (used when the chain hits BYLAYER).
 * @param layerColors Layer table for the active drawing.
 * @param blockColor  Resolved colour string of the parent INSERT for the
 *                    BYBLOCK case, or `null` (root scope, or a BYLAYER caller).
 */
function resolveByLayerOrBlockHex(
  layerName: string,
  layerColors: LayerColorMap,
  blockColor: string | null,
  foregroundHex: string = FALLBACK_HEX,
): string {
  // ── BYBLOCK ── walk up to the parent INSERT colour (skipped for a BYLAYER
  // caller, which passes blockColor = null), then fall through to BYLAYER.
  if (blockColor !== null) {
    const parsed = parseLiteralColor(blockColor, foregroundHex);
    if (parsed) return parsed;
  }

  // ── BYLAYER (explicit, or BYBLOCK fallback above) ──
  // A layer whose own ACI is 7 hands the entity the LAYOUT foreground, not a
  // fixed white — ezdxf `LayerProperties.get_entity_color_from_layer(fg)`.
  // `parseLiteralColor` applies that for us.
  const record = layerColors.get(layerName.toLowerCase());
  if (record) {
    const parsed = parseLiteralColor(record.color, foregroundHex);
    if (parsed) return parsed;
  }
  // ezdxf `_true_entity_color`: an unknown / invalid colour falls back to the
  // layout's `default_color`, i.e. the same foreground ACI 7 resolves to.
  return foregroundHex;
}

/**
 * Walk the color resolution chain.
 *
 * @param entityColor   Raw `Color` value from entity.properties (may be
 *                      `"BYLAYER"`, `"BYBLOCK"`, ACI text, hex, etc.).
 * @param layerName     Entity's layer (used when the chain hits `BYLAYER`).
 * @param layerColors   Layer table for the active drawing.
 * @param blockColor    Resolved colour of the parent INSERT (when the
 *                      entity is inside a block) — used when the chain
 *                      hits `BYBLOCK`. `null` at root scope.
 *
 * @returns A `#rrggbb` lowercase hex string. Never throws — falls back
 *          to white when no level of the chain produces a value.
 */
export function resolveColorHex(
  entityColor: string | undefined,
  layerName: string,
  layerColors: LayerColorMap,
  blockColor: string | null = null,
  foregroundHex: string = FALLBACK_HEX,
): string {
  const raw = (entityColor ?? "").trim();
  const upper = raw.toUpperCase();

  // ── BYBLOCK / BYLAYER ── defer to the shared walk. BYBLOCK ("BYBLOCK"/"0")
  // consults the parent INSERT colour first; BYLAYER ("BYLAYER"/"256"/"")
  // passes blockColor = null so the walk skips straight to the layer lookup —
  // byte-identical to the prior inline two-branch logic.
  if (upper === "BYBLOCK" || upper === "0") {
    return resolveByLayerOrBlockHex(
      layerName,
      layerColors,
      blockColor,
      foregroundHex,
    );
  }
  if (upper === "BYLAYER" || upper === "256" || upper === "") {
    return resolveByLayerOrBlockHex(layerName, layerColors, null, foregroundHex);
  }

  // ── Literal colour (ACI / hex) ──
  const parsed = parseLiteralColor(raw, foregroundHex);
  return parsed ?? foregroundHex;
}

/**
 * Format a valid ACI index (1-255) to a lowercase `#rrggbb` (or `FALLBACK_HEX`).
 * Matches `parseLiteralColor`'s integer path for valid ACI EXACTLY — `aciToHex`
 * returns the palette value in UPPERCASE (`aciColorMap.ts`), and the legacy
 * string path lowercases it (`parseLiteralColor` line "hex.toLowerCase()"), so
 * the union-direct path MUST `.toLowerCase()` too or it would diverge from the
 * legacy path on case (`#FF0000` vs `#ff0000`).
 *
 * Out-of-domain `aci` returns `FALLBACK_HEX` deterministically:
 *   - The symbolic codes 0 / 256 are reinterpreted by the `index` arm's
 *     guards (→ byBlock / byLayer) BEFORE this helper is reached, so they never
 *     arrive here. Negative layer-off codes and 256+ ints are out of the 1-255
 *     palette range and return `FALLBACK_HEX`.
 *   - The `< 1 || > 255` gate makes the fallback explicit rather than leaning on
 *     `aciToHex` returning null. NOTE this is INTENTIONALLY NOT byte-identical
 *     to the legacy round-trip on the corrupt 6-digit-decimal band (100000+):
 *     `colorToLegacyString(index)`→`String(aci)`→`parseLiteralColor` MISREADS a
 *     6-digit decimal as a bare hex colour (`"123456"`→`#123456`). That legacy
 *     behaviour is itself a bug on out-of-spec garbage ACI; the one-truth typed
 *     path returns `FALLBACK_HEX` for ALL out-of-range ACI instead of
 *     replicating the misread. Valid ACI (1-255) is byte-identical in both paths.
 */
function aciIndexToHex(
  aci: number,
  foregroundHex: string = FALLBACK_HEX,
): string {
  if (aci < 1 || aci > 255) return foregroundHex;
  // ACI 7 is the background contrast colour — the typed twin of
  // `parseLiteralColor`'s 7-arm (ezdxf `_aci_to_true_color`).
  if (aci === ACI_FOREGROUND) return foregroundHex;
  return aciToHex(aci)?.toLowerCase() ?? foregroundHex;
}

/**
 * EPIC-COLOR-UNION S2 — UNION-DIRECT colour resolution from the canonical
 * `DrawingEntity`. This is the ONE-TRUTH typed reader: it resolves the typed
 * `EntityColor` union DIRECTLY (no `colorToLegacyString` string round-trip),
 * sharing the BYLAYER / BYBLOCK walk (`resolveByLayerOrBlockHex`) with the
 * legacy string-bag `resolveColorHex` so the two paths stay byte-identical for
 * the dominant common cases (byLayer / byBlock / index), while a true-colour
 * (`rgb`) entity resolves to its real `#rrggbb` and a colour-book (`bookColor`)
 * entity resolves to its ACI fallback.
 *
 * Per-kind resolution:
 *   - `byLayer`   → shared walk (layer lookup; blockColor irrelevant → null).
 *   - `byBlock`   → shared walk consulting the parent INSERT colour first.
 *   - `index`     → `aciIndexToHex(aci)` for a VALID ACI (1-255): lowercase,
 *                   byte-identical to the legacy `parseLiteralColor` integer
 *                   path. The symbolic codes 0 / 256 (never emitted as `index`
 *                   by any codec — `coerceColor` routes them to byBlock / byLayer
 *                   first) are reinterpreted here EXACTLY like the legacy
 *                   round-trip would (`String(0)`→BYBLOCK chain, `String(256)`→
 *                   BYLAYER chain) for defense-in-depth whole-union byte-identity.
 *   - `rgb`       → the true `#rrggbb` from the channels (the visible S2 win).
 *   - `bookColor` → its ACI fallback, projected EXACTLY like `colorToLegacyString`
 *                   (aci undefined/256 → BYLAYER chain; aci 0 → BYBLOCK chain;
 *                   else the index hex). There is intentionally NO colour-book
 *                   (RAL / PANTONE) palette lookup here — the 430 swatch palette
 *                   is a future slice; bookColor uses ONLY its 62 fallback.
 *
 * @param entity      Canonical typed entity (carries `color` + `layer`).
 * @param layerColors Layer table for the active drawing.
 * @param blockColor  Resolved colour of the parent INSERT (BYBLOCK chain),
 *                    or `null` at root scope.
 */
export function resolveColorHexFromEntity(
  entity: Pick<DrawingEntity, "color" | "layer">,
  layerColors: LayerColorMap,
  blockColor: string | null = null,
  foregroundHex: string = FALLBACK_HEX,
): string {
  // `coerceColor` guard (matches every peer call site) — degrades a slipped
  // raw-string `color` to a typed union before the switch.
  const color = coerceColor(entity.color);
  switch (color.kind) {
    case "byLayer":
      // Layer lookup only; the parent INSERT colour is irrelevant for BYLAYER.
      return resolveByLayerOrBlockHex(
        entity.layer,
        layerColors,
        null,
        foregroundHex,
      );
    case "byBlock":
      // Walk the parent INSERT colour first (same chain as the legacy "0").
      return resolveByLayerOrBlockHex(
        entity.layer,
        layerColors,
        blockColor,
        foregroundHex,
      );
    case "index":
      // Defense-in-depth byte-identity with the legacy round-trip across the
      // WHOLE union type (not just the codec-producible subset). No codec emits
      // a symbolic ACI as `kind:"index"` (`coerceColor`/`colorFromDxf` route
      // 0→byBlock, 256→byLayer before ever producing an index — color.ts:124-125),
      // but a hand-forged literal could; mirror the legacy reinterpretation that
      // `colorToLegacyString(index)`→`String(aci)`→`resolveColorHex` would apply,
      // so the two paths agree even on that non-producible input:
      //   aci 0   → "0"   → BYBLOCK chain (parent INSERT colour, then layer)
      //   aci 256 → "256" → BYLAYER chain (layer lookup)
      if (color.aci === 0) {
        return resolveByLayerOrBlockHex(
          entity.layer,
          layerColors,
          blockColor,
          foregroundHex,
        );
      }
      if (color.aci === 256) {
        return resolveByLayerOrBlockHex(
          entity.layer,
          layerColors,
          null,
          foregroundHex,
        );
      }
      return aciIndexToHex(color.aci, foregroundHex);
    case "rgb":
      // The S2 visible win — render the real 24-bit true colour. Lowercase
      // `#rrggbb`, byte-identical to `colorToLegacyString(rgb)` →
      // `parseLiteralColor` which the legacy path already produces.
      return `#${hex2(color.r)}${hex2(color.g)}${hex2(color.b)}`;
    case "bookColor": {
      // NO colour-book palette (430 swatch deferred to a future slice) — fall
      // back to the coexisting 62, projected EXACTLY like `colorToLegacyString`
      // so a 430+62 entity resolves to the SAME ACI-fallback hex the legacy
      // round-trip produced (NOT a layer colour, when 62 is a real index).
      if (color.aci === undefined || color.aci === 256) {
        return resolveByLayerOrBlockHex(
          entity.layer,
          layerColors,
          null,
          foregroundHex,
        );
      }
      if (color.aci === 0) {
        return resolveByLayerOrBlockHex(
          entity.layer,
          layerColors,
          blockColor,
          foregroundHex,
        );
      }
      return aciIndexToHex(color.aci, foregroundHex);
    }
  }
}

/** Two-digit lowercase hex for a 0-255 channel (local — `color.ts`'s `hex2`
 * is module-private; a 2-char format is below jscpd's clone threshold and
 * avoids importing the whole `colorToLegacyString` round-trip). */
function hex2(n: number): string {
  return (n & 0xff).toString(16).padStart(2, "0");
}

/**
 * Build a `LayerColorMap` from the editor-store layer-records list.
 * The keys are lower-cased layer names so the resolver doesn't need to
 * worry about case mismatches between entity.layer and the table.
 */
export function buildLayerColorMap(
  layers: Iterable<LayerColorRecord>,
): LayerColorMap {
  const map = new Map<string, LayerColorRecord>();
  for (const layer of layers) {
    map.set(layer.name.toLowerCase(), layer);
  }
  return map;
}

/**
 * Build a `LayerColorMap` straight from the drawing's `LayerDefinition`
 * table (`styleTables.layers`) — the RAW shape every scene3d caller
 * subscribes to off the editor store, where `color` is a NUMERIC ACI
 * index (`LayerDefinition.color: number`), NOT the `LayerColorRecord.color`
 * STRING this module's resolvers parse.
 *
 * fix/bylayer-colour-never-resolves-on-canvas — `SceneContent.tsx` and
 * `draftPreviewLayer.tsx` both used to call `buildLayerColorMap([])`
 * outright (an inert stub predating the PR-I layer-table wiring), so
 * `resolveColorHex`'s BYLAYER branch always missed the lookup and every
 * entity fell back to `FALLBACK_HEX` (`#ffffff`) regardless of its layer's
 * authored colour. The PDF plot path (`print/pdf/sheetRenderer.ts`) never
 * had this bug — it already builds its `ctx.layerColors` with the exact
 * `String(l.color)` conversion this helper centralizes, so scene3d and PDF
 * output agree once both go through it.
 *
 * Passing `LayerDefinition[]` straight to `buildLayerColorMap` (skipping
 * the `String()` conversion) reproduces the SAME silent-empty-lookup bug
 * with more code: `parseLiteralColor` only accepts a `string`, so a numeric
 * `color` field fails every branch (`.trim()` on a number throws, or with a
 * lenient cast every entry parses to `null`) and the lookup is effectively
 * empty again even though the `Map` itself has entries.
 */
export function buildLayerColorMapFromLayerDefinitions(
  layers: Iterable<{ name: string; color?: number }>,
): LayerColorMap {
  const records: LayerColorRecord[] = [];
  for (const layer of layers) {
    records.push({ name: layer.name, color: String(layer.color) });
  }
  return buildLayerColorMap(records);
}
